class LuxuryKeySolutions extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-lks-root]');
    this.tabButtons = Array.from(this.querySelectorAll('[data-lks-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-lks-panel]'));
    this.mobileSelect = this.querySelector('[data-lks-select]');
    this.enableAnimation = this.dataset.enableAnimation === 'true';
    this.activeId = this.dataset.defaultTab || this.tabButtons[0]?.dataset.lksTab || '';
    this.isInView = false;

    this.onTabClick = this.onTabClick.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);

    this.tabButtons.forEach((button) => button.addEventListener('click', this.onTabClick));
    this.mobileSelect?.addEventListener('change', this.onSelectChange);

    this.setupInViewObserver();

    if (this.activeId) {
      this.activate(this.activeId, false);
      if (this.enableAnimation) {
        window.requestAnimationFrame(() => this.playEntrance(this.getActivePanel()));
      }
    }
  }

  disconnectedCallback() {
    this.inViewObserver?.disconnect();
    this.tabButtons.forEach((button) => button.removeEventListener('click', this.onTabClick));
    this.mobileSelect?.removeEventListener('change', this.onSelectChange);
  }

  setupInViewObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isInView = true;
      return;
    }

    this.inViewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const wasInView = this.isInView;
          this.isInView = entry.isIntersecting;

          if (entry.isIntersecting && !wasInView && this.enableAnimation) {
            this.playEntrance(this.getActivePanel());
          }
        });
      },
      { threshold: 0.2 }
    );

    this.inViewObserver.observe(this);
  }

  getActivePanel() {
    return this.panels.find((panel) => panel.dataset.lksPanel === this.activeId);
  }

  onTabClick(event) {
    const id = event.currentTarget?.dataset?.lksTab;
    if (!id || id === this.activeId) {
      if (id === this.activeId && this.enableAnimation) {
        this.playEntrance(this.getActivePanel());
      }
      return;
    }
    this.activate(id, true);
  }

  onSelectChange(event) {
    const id = event.target?.value;
    if (!id || id === this.activeId) return;
    this.activate(id, true);
  }

  playEntrance(panel) {
    if (!this.enableAnimation || !panel) return;

    panel.classList.add('is-replaying');
    void panel.offsetHeight;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        panel.classList.remove('is-replaying');
      });
    });
  }

  activate(id, userTriggered) {
    this.activeId = id;

    this.tabButtons.forEach((button) => {
      const isActive = button.dataset.lksTab === id;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    let activePanel = null;

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.lksPanel === id;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) activePanel = panel;
    });

    if (this.mobileSelect && this.mobileSelect.value !== id) {
      this.mobileSelect.value = id;
    }

    if (userTriggered && activePanel) {
      this.playEntrance(activePanel);
    }
  }
}

if (!customElements.get('luxury-key-solutions')) {
  customElements.define('luxury-key-solutions', LuxuryKeySolutions);
}
